import getIntl from 'Helpers/intl'
import { logAppError } from 'Helpers/logger'
import {
  storeGet,
  STORE_KEY_ID_LIST,
  STORE_KEY_LIST
} from 'mailup-react-helpers'
import {
  checkIsOnSend,
  getArrayOfSourcesByIdList,
  getEmailCampaignById,
  getFiltersNamesByFilterId,
  getGroupsNamesByGroupsIds,
  getListOfSources,
  getMessageById,
  getMessageThumbnail,
  getSourceBySourceId,
  insertEmailCampaign,
  insertSource,
  setArrayOfListsByIdSource,
  updateContents,
  updateEmailCampaign
} from '../actions/actions'
import { campaignState } from '../state/campaign'
import {
  modalConcurrentAccess,
  modalConfirmEditMessageState,
  modalConfirmIgnoreChangesState,
  modalEmptySourceState,
  modalExitEditMessageState,
  modalFilterDetailsState,
  modalLimitDataContentState,
  modalNewSourceState,
  modalThirdPartyState
} from '../state/modals'
import {
  checkFrequencySelector,
  checkFrequencyState,
  messageIsOnSentState,
  pageState,
  segmentationFiltersState,
  sendingRowData,
  sourceListState,
  stepState,
  thumbnailState,
  wizardSteps
} from '../state/page'
import { toastShow, TOAST_VARIANT_ERROR } from 'mailup-web-components'
import { getTemplateById } from '../actions/carouselActions'
import { beePluginConfigState, beePluginState, editorState } from '../state/bee'
import { beListGetList } from 'Api/beListApi'
import { getMessageDetails } from '../actions/actionEditBee'
import {
  filtersState,
  groupsState,
  previewLastSelected,
  previewState
} from '../state/preview'
import { templatePreviewState, templatesState } from '../state/templates'
import { carouselState } from 'Components/Carousel/recoil/carousel'

export const resetStateCallback =
  ({ reset }) =>
  async () => {
    // page.js
    reset(pageState)
    reset(wizardSteps)
    reset(sourceListState)
    reset(segmentationFiltersState)
    reset(stepState)
    reset(checkFrequencyState)
    reset(sendingRowData)
    reset(thumbnailState)
    reset(messageIsOnSentState)

    // bee.js
    reset(editorState)
    reset(beePluginState)
    reset(beePluginConfigState)

    // campaign.js
    reset(campaignState)

    // modals. js
    reset(modalNewSourceState)
    reset(modalConfirmEditMessageState)
    reset(modalExitEditMessageState)
    reset(modalEmptySourceState)
    reset(modalLimitDataContentState)
    reset(modalConfirmIgnoreChangesState)
    reset(modalConcurrentAccess)
    reset(modalThirdPartyState)
    reset(modalFilterDetailsState)

    // preview.js
    reset(previewState)
    reset(groupsState)
    reset(filtersState)
    reset(previewLastSelected)

    // templates.js
    reset(templatesState)
    reset(templatePreviewState)

    // carousel
    reset(carouselState)
  }

export const changeWizardStepCallback =
  ({ set, snapshot }) =>
  async (nextStep) => {
    set(wizardSteps, (prev) => ({
      ...prev,
      wizardStepsDone:
        nextStep > prev.wizardStepsDone ? nextStep - 1 : prev.wizardStepsDone,
      wizardStep: nextStep
    }))
  }

export const initCallback =
  ({ set, snapshot }) =>
  async (idCampaign) => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true,
      loaded: false
    }))
    let ret

    try {
      const idList = storeGet(STORE_KEY_ID_LIST)

      if (idCampaign) {
        const campaign = await getEmailCampaignById(idCampaign)
        set(campaignState, (prev) => ({ ...prev, ...campaign }))
        setCheckFrequency({ set }, campaign.CheckFrequency)
        ret = campaign
      }

      const [arrayOfSourcesOfList, arrayOfSourcesShared] = await Promise.all([
        getArrayOfSourcesByIdList(idList),
        getArrayOfSourcesByIdList(0)
      ])

      const filterSourceIds = arrayOfSourcesOfList
        ? arrayOfSourcesOfList.concat(arrayOfSourcesShared || [])
        : []

      const source = await getListOfSources({
        pageSize: 0,
        pageNumber: 1,
        filterSourceIds,
        orderBy: 'UpdateDate',
        orderDescending: true
      })

      const sourceList = source
        ? source.Items.map((m) => ({
            label:
              getIntl().formatMessage({
                id: 'Load.MailupCommon.Id'
              }) +
              m.Id +
              ' - ' +
              m.SourceName,
            value: m.Id
          }))
        : []

      set(sourceListState, {
        sourceList
      })
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'getCampaignCallback',
        appError: error
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false,
      loaded: true
    }))

    return ret
  }

export const addSourceCallback =
  ({ set, snapshot, reset }) =>
  async () => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const idList = storeGet(STORE_KEY_ID_LIST)
      const source = await snapshot.getPromise(modalNewSourceState)

      const idSource = await insertSource({
        SourceAddress: source.sourceUrl,
        SourceType: 1,
        SourceName: source.sourceName,
        UpdateType: 1,
        UpdateFrequency: '*/10 * * * *',
        IsEnabled: true
      })
      set(campaignState, (prev) => ({
        ...prev,
        IdSource: idSource,
        idSouceIsDirty: true
      }))

      await setArrayOfListsByIdSource(idSource, {
        ArrayOfListId: [idList]
      })

      const [arrayOfSourcesOfList, arrayOfSourcesShared] = await Promise.all([
        getArrayOfSourcesByIdList(idList),
        getArrayOfSourcesByIdList(0),
        updateContents(idSource)
      ])
      const filterSourceIds = arrayOfSourcesOfList.concat(
        arrayOfSourcesShared || []
      )

      if (filterSourceIds.length > 0) {
        const source = await getListOfSources({
          pageSize: 0,
          pageNumber: 1,
          filterSourceIds,
          orderBy: 'UpdateDate',
          orderDescending: true
        })

        const sourceList = source.Items.map((m) => ({
          label:
            getIntl().formatMessage({
              id: 'Load.MailupCommon.Id'
            }) +
            m.Id +
            ' - ' +
            m.SourceName,
          value: m.Id
        }))

        set(sourceListState, {
          sourceList
        })
      }
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'addSourceCallback',
        appError: error
      })
      toastShow(
        getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric'
        }),
        TOAST_VARIANT_ERROR,
        'addSourceCallbackError'
      )
    }

    reset(modalNewSourceState)

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const saveCampaignCallback =
  ({ set, snapshot }) =>
  async (nextStep) => {
    saveCampaign(nextStep, set, snapshot)
  }

export async function saveCampaign(
  nextStep,
  set,
  snapshot,
  idMessageSavedFromBee
) {
  set(pageState, (prev) => ({
    ...prev,
    loading: true
  }))

  try {
    const campaign = await snapshot.getPromise(campaignState)
    if (campaign.Id === 0) {
      const idCampaign = await insertEmailCampaign(campaign)
      const campaignData = await getEmailCampaignById(idCampaign)
      set(campaignState, (prev) => ({
        ...prev,
        ...campaignData,
        Id: idCampaign
      }))
      setCheckFrequency({ set }, campaignData.CheckFrequency)
    } else {
      let updatedCampaignData

      if (nextStep === 4) {
        let timezone
        let checkFrequency = ''

        if (campaign.CheckFrequencyType === 2) {
          checkFrequency = await snapshot.getPromise(checkFrequencySelector)

          set(campaignState, (prev) => ({
            ...prev,
            CheckFrequency: checkFrequency
          }))
        }
        if (campaign.CheckFrequencyType === 0) {
          set(campaignState, (prev) => ({
            ...prev,
            CheckFrequencyType: 1
          }))
        }
        if (campaign.Timezone === '') {
          const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)
          timezone = tzcode
          set(campaignState, (prev) => ({
            ...prev,
            Timezone: timezone
          }))
        } else {
          timezone = campaign.Timezone
        }

        updatedCampaignData = {
          ...campaign,
          CheckFrequencyType: campaign.CheckFrequencyType === 2 ? 2 : 1,
          Timezone: timezone,
          CheckFrequency: checkFrequency
        }
      } else {
        updatedCampaignData = campaign
      }

      if (idMessageSavedFromBee) {
        // nel salvataggio dal save del bee è stato creato un nuovo messaggio email
        // lo stato della campagna ancora non lo contiene
        updatedCampaignData = {
          ...campaign,
          EmailOptions: {
            ...campaign.EmailOptions,
            IdMessage: idMessageSavedFromBee
          }
        }
        set(campaignState, (prev) => ({
          ...prev,
          EmailOptions: {
            ...prev.EmailOptions,
            IdMessage: idMessageSavedFromBee
          }
        }))
      }

      updateEmailCampaign(campaign.Id, updatedCampaignData)
    }

    if (nextStep === 'EDIT') {
      set(pageState, (prev) => ({
        ...prev,
        step2SelectTemplate: false,
        forceTemplates: false
      }))
    } else if (nextStep === 'END') {
      set(wizardSteps, (prev) => ({
        ...prev,
        wizardStepsDone: 4,
        wizardStep: 4
      }))
      set(pageState, (prev) => ({ ...prev, isEnd: true }))
    } else {
      set(wizardSteps, (prev) => ({
        ...prev,
        wizardStepsDone:
          nextStep > prev.wizardStepsDone ? nextStep - 1 : prev.wizardStepsDone,
        wizardStep: nextStep
      }))
    }
    set(campaignState, (prev) => ({
      ...prev,
      isDirty: false
    }))
  } catch (error) {
    logAppError({
      Component: 'Campaign',
      Method: 'getCampaignCallback',
      appError: error
    })

    set(pageState, (prev) => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }

  set(pageState, (prev) => ({
    ...prev,
    loading: false
  }))
}

export const getTemplateByIdCallback =
  ({ set, snapshot }) =>
  async () => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const campaign = await snapshot.getPromise(campaignState)

      const { Structure } = await getTemplateById(
        campaign.EmailOptions.IdTemplate
      )

      set(editorState, (prev) => ({ ...prev, initialStructure: Structure }))
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'getCampaignCallback',
        appError: error
      })

      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const onSelectErrorCallback =
  ({ set, snapshot }) =>
  async (error) => {
    logAppError({
      appError: error,
      Component: 'Campaign-Step3-Choose',
      Method: 'onSelectErrorCallback'
    })

    set(pageState, (prev) => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' })
    }))
  }

export const setCheckFrequency = ({ set }, checkFrequency) => {
  if (checkFrequency) {
    const splittedFrequency = checkFrequency.split(' ')
    const m = splittedFrequency[0]
    const h = splittedFrequency[1]
    const d = splittedFrequency[4].split(',').map((elem) => parseInt(elem))
    set(checkFrequencyState, (prev) => ({ ...prev, h, m, d }))
  }
}

export const getSenderListInfoDetailsCallback =
  ({ set, snapshot }) =>
  async () => {
    const idList = storeGet(STORE_KEY_ID_LIST)
    set(stepState, (prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        senderInfo: {
          ...prev.step3.senderInfo,
          listInfoDetailsLoaded: false,
          listInfoDetailsLoading: true
        }
      }
    }))

    const {
      EmailOptions: {
        SenderEmail: campaignEmail,
        SenderName: campaignName,
        ReplyTo: campaignReplyTo
      }
    } = await snapshot.getPromise(campaignState)
    try {
      if (!campaignEmail && !campaignName && !campaignReplyTo) {
        const {
          SenderName,
          SenderEmail,
          SenderReplyTo: ReplyTo
        } = await beListGetList(idList)

        set(campaignState, (prev) => ({
          ...prev,
          EmailOptions: {
            ...prev.EmailOptions,
            SenderEmail,
            SenderName,
            ReplyTo
          }
        }))
      } else {
        // prendo i dati dalla campagna
        set(campaignState, (prev) => ({
          ...prev,
          EmailOptions: {
            ...prev.EmailOptions,
            SenderEmail: campaignEmail,
            SenderName: campaignName,
            ReplyTo: campaignReplyTo
          }
        }))
      }
    } catch (error) {
      logAppError({
        appError: error,
        Component: 'Campaign Step3 Sender Info',
        Method: 'getSenderListInfoDetailsCallback'
      })
      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' })
      }))
    }

    set(stepState, (prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        senderInfo: {
          ...prev.step3.senderInfo,
          listInfoDetailsLoaded: true,
          listInfoDetailsLoading: false
        }
      }
    }))
  }

export const getMessageStructureCallback =
  ({ set, snapshot }) =>
  async () => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true
    }))

    try {
      const campaign = await snapshot.getPromise(campaignState)

      const { Structure } = await getMessageDetails(
        campaign.EmailOptions.IdMessage
      )

      set(editorState, (prev) => ({ ...prev, initialStructure: Structure }))
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'getCampaignCallback',
        appError: error
      })

      set(pageState, (prev) => ({
        ...prev,
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false
    }))
  }

export const fetchStep4DataCallback =
  ({ set, snapshot }) =>
  async () => {
    const {
      IdSource,
      EmailOptions: { ApplyFilter, IncludeGroups, ExcludeGroups }
    } = await snapshot.getPromise(campaignState)

    try {
      const [
        { sourceName, sourceType },
        includeGroups,
        excludeGroups,
        applyFilters
      ] = await Promise.all([
        getSourceBySourceId(IdSource),
        getGroupsNamesByGroupsIds(IncludeGroups),
        getGroupsNamesByGroupsIds(ExcludeGroups),
        getFiltersNamesByFilterId(ApplyFilter)
      ])

      if (IncludeGroups && IncludeGroups.includes(0)) {
        includeGroups.unshift(
          `${getIntl().formatMessage({
            id: 'Load.SearchRecipients.nessunGruppo'
          })}`
        )
      }
      if (ExcludeGroups && ExcludeGroups.includes(0)) {
        excludeGroups.unshift(
          `${getIntl().formatMessage({
            id: 'Load.SearchRecipients.nessunGruppo'
          })}`
        )
      }

      set(sendingRowData, (prev) => ({
        ...prev,
        sourceName,
        sourceType,
        includeGroups,
        excludeGroups,
        applyFilters
      }))
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'fetchStep4DataCallback',
        appError: error
      })
    }

    try {
      const {
        EmailOptions: { IdMessage }
      } = await snapshot.getPromise(campaignState)
      const res = await checkIsOnSend(IdMessage)

      set(messageIsOnSentState, (prev) => ({
        ...prev,
        isOnSent: res,
        showMessageBox: res
      }))
    } catch (error) {
      logAppError({
        Component: 'Campaign',
        Method: 'fetchStep4DataCallback',
        appError: error
      })
    }
  }

export const fetchStep4ThumbnailCallback =
  ({ set, snapshot }) =>
  async () => {
    try {
      set(thumbnailState, (prev) => ({ ...prev, state: 'loading' }))
      const {
        UpdateDate,
        EmailOptions: { IdMessage },
        Id
      } = await snapshot.getPromise(campaignState)

      let timeStamp = UpdateDate
        ? new Date(UpdateDate)
            .toISOString()
            .replace(/[-:.TZ]/g, '')
            .substring(0, 14)
        : '00000000000000'
      let previewUrl = ''

      const hasThumbnail = await getMessageById(IdMessage)

      if (!hasThumbnail) {
        const res = await getMessageThumbnail(IdMessage)
        if (res) {
          const { UpdateDate: newUpdateDate } = await getEmailCampaignById(Id)
          timeStamp = new Date(newUpdateDate)
            .toISOString()
            .replace(/[-:.TZ]/g, '')
            .substring(0, 14)
          previewUrl = `/messagepreview/${IdMessage}_268x321.jpg?ts=${timeStamp}`
        } else {
          previewUrl = '/css/img/no-image-preview-backgroud.png'
        }
      } else {
        previewUrl = `/messagepreview/${IdMessage}_268x321.jpg?ts=${timeStamp}`
      }
      set(thumbnailState, (prev) => ({
        ...prev,
        url: previewUrl,
        state: 'loaded'
      }))
    } catch (error) {
      set(thumbnailState, (prev) => ({ ...prev, state: 'error' }))
      logAppError({
        Component: 'Campaign',
        Method: 'fetchStep4ThumbnailCallback',
        appError: error
      })
    }
  }
